import SimpleGrid from "@ui-kit/layout/Grid/SimpleGrid.web";
import { useIsSmallerThanSm } from "@shared/hooks/useIsSmallerThan";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { CalendarBadge } from "@shared/components/application/data-display/badges/CalendarBadge/CalendarBadge";
import { RenewalBadge } from "@shared/components/application/data-display/badges/RenewalBadge/RenewalBadge";
import { SecureBadge } from "@shared/components/application/data-display/badges/SecureBadge/SecureBadge";
import { DiscountBadge } from "@shared/components/application/data-display/badges/DiscountBadge/DiscountBadge";
import { InstantConfirmationBadge } from "@shared/components/application/data-display/badges/InstantConfirmationBadge/InstantConfirmationBadge";
import { PaymentPlansBadge } from "@shared/components/application/data-display/badges/PaymentPlansBadge/PaymentPlansBadge";
import { PriceRangeBadge } from "@shared/components/application/data-display/badges/PriceRangeBadge/PriceRangeBadge";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";

export interface MembershipBadge {
  id: number;
  icon: JSX.Element;
  text: string | JSX.Element;
  showBadge: boolean;
  dataTestId: string;
}

export interface MembershipDetailsHeaderBadgesProps {
  membership?: PublicMembershipGroupSerializer | undefined;
  venue?: PublicVenueSerializer | undefined;
  condensed?: boolean;
}

const getBadges = ({
  membership,
  venue,
}: MembershipDetailsHeaderBadgesProps) => {
  const isSmallerThanSm = useIsSmallerThanSm();

  if (!membership) {
    return null;
  }

  return (
    <>
      {membership.current_season_on_sale && (
        <CalendarBadge
          timezone={venue?.timezone}
          startDate={membership.current_season_on_sale?.start_date}
          endDate={membership.current_season_on_sale?.end_date}
        />
      )}

      <RenewalBadge
        isVisible={Boolean(membership.renewal_frequency)}
        renewalFrequency={membership.renewal_frequency}
      />

      <SecureBadge />

      <DiscountBadge
        isVisible={Boolean(membership.benefit_data?.discount?.exists)}
      />

      <InstantConfirmationBadge />

      {/* Hide until membership.has_payment_plans exists */}
      <PaymentPlansBadge isVisible={false} />

      <PriceRangeBadge
        isVisible={isSmallerThanSm}
        items={membership.membership_levels}
        currency={membership.venue?.currency}
        allInPricingIsEnabled={membership.venue?.all_in_pricing_is_enabled}
      />
    </>
  );
};

export const MembershipDetailsHeaderBadges = ({
  membership,
  venue,
  condensed,
}: MembershipDetailsHeaderBadgesProps) => {
  const isSmallerThanSm = useIsSmallerThanSm();

  const badges = getBadges({ membership, venue });
  const mt = isSmallerThanSm || condensed ? "8px" : "16px";
  const fontSize = isSmallerThanSm || condensed ? "12px" : "14px";
  const columns = isSmallerThanSm ? 1 : 2;

  return (
    <SimpleGrid mt={mt} fontSize={fontSize} columns={columns}>
      {badges}
    </SimpleGrid>
  );
};
